import { useEffect, useMemo, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _some from 'lodash/some';
import { useQuery } from '@apollo/client';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { useLatest } from 'react-use';
import CheckboxGroupField from '../forms/checkbox-group-field';
import { eventSeriesConvertibleEventsQuery } from '../../apollo/event-series';
import { dateAndTimeRange, momentParse } from '../../utils/time';
import MaterialLinkButton from '../material-link-button';

const useStyles = makeStyles((theme) => ({
  label: {
    display: 'flex',
    alignItems: 'center',
  },
  toggleButton: {
    marginLeft: theme.spacing(1),
  },
  noConvertibleFound: {
    fontStyle: 'italic',
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const EventSeriesFormConvertEvents = ({ disabled, touched, ...rest }) => {
  const classes = useStyles();

  const {
    values: { hostType, groupId, convertEventIds },
    setFieldValue,
    errors: formErrors,
    touched: formTouched,
  } = useFormikContext();

  const {
    data: convertibleEventsData,
    loading: convertibleEventsLoading,
  } = useQuery(eventSeriesConvertibleEventsQuery, {
    variables: {
      hostType,
      groupId,
    },
  });

  const options = useMemo(() => {
    if (!convertibleEventsData) {
      return [];
    }
    return _map(convertibleEventsData.eventSeriesConvertibleEvents, (event) => {
      const startAt = momentParse(event.startAt, event.timezone);
      const endAt = event.endAt ? momentParse(event.endAt, event.timezone) : null;
      const pastEndAt = endAt || startAt;
      const past = momentParse().isAfter(pastEndAt);
      return {
        value: event.id,
        label: `${event.name} (${dateAndTimeRange(startAt, endAt)})${past ? ' - PAST' : ''}`,
      };
    });
  }, [convertibleEventsData]);

  const latestConvertEventIds = useLatest(convertEventIds);
  useEffect(() => {
    if (convertibleEventsLoading) {
      return;
    }
    const validEventIds = _filter(latestConvertEventIds.current, (id) => {
      return _some(options, (o) => o.value === id);
    });
    if (validEventIds.length !== convertEventIds.length) {
      setFieldValue('convertEventIds', validEventIds);
    }
  }, [options, convertibleEventsLoading]);

  const handleToggleAll = useCallback(() => {
    if (!convertibleEventsData) {
      return;
    }
    if (convertEventIds.length === convertibleEventsData.eventSeriesConvertibleEvents.length) {
      setFieldValue('convertEventIds', []);
    } else  {
      setFieldValue('convertEventIds', _map(convertibleEventsData.eventSeriesConvertibleEvents, 'id'));
    }
  }, [convertEventIds, convertibleEventsData]);

  if (convertibleEventsLoading) {
    return <p>Loading...</p>;
  }
  if (!options.length) {
    return (
      <Fragment>
        <p className={classes.noConvertibleFound}>
          No events found with the specified Host and Group.
        </p>
        <MaterialLinkButton
          href="/events/new"
          variant="contained"
        >
          Create New Event
        </MaterialLinkButton>
        {(touched || formTouched.convertEventIds) && Boolean(formErrors.convertEventIds) && (
          <p className={classes.error}>
            {formErrors.convertEventIds}
          </p>
        )}
      </Fragment>
    )
  }

  return (
    <CheckboxGroupField
      name="convertEventIds"
      label={
        <div className={classes.label}>
          Convert Existing Events
          <Button
            onClick={handleToggleAll}
            variant="contained"
            size="small"
            className={classes.toggleButton}
          >
            Toggle All
          </Button>
        </div>
      }
      options={options}
      disabled={disabled || !convertibleEventsData}
      touched={touched}
      {...rest}
    />
  );
};

EventSeriesFormConvertEvents.propTypes = {
  disabled: PropTypes.bool,
  touched: PropTypes.bool,
};

export default EventSeriesFormConvertEvents;
